import React, { useState, useEffect, useMemo } from "react";
import { Dialog, Flex, Text, Button, Select, Separator, Box, Switch } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../context/ToastContext";
import { ToastAlert } from "components";
import { useProfileContext, useLocationContext } from "context";
import { useGetAllBrands, useSaveBloomreachMappings } from "hooks";
import { BloomreachConsentSection } from "./BloomreachConsentSection";
import { BloomreachDataFieldsSection } from "./BloomreachDataFieldsSection";
import { normalizeBloomreachFieldMappings } from "./bloomreachFieldMappings";
import {
  BLOOMREACH_SKIP,
  BLOOMREACH_SYNC,
  BloomreachColumnGrid,
  BloomreachLocationRow,
  BloomreachMappingHeader,
} from "./BloomreachLocationRow";

function locationId(loc) {
  return String(loc.id ?? loc.value ?? loc.location_id);
}

export function BloomreachMappingDialog({
  isOpen,
  onOpenChange,
  initialMappings = [],
  initialFieldMappings = null,
  initialSyncConsent = true,
  initialConsentCategories = null,
  reconnectAction = null,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();
  const { userProfile } = useProfileContext();
  const { data: brandsData } = useGetAllBrands(userProfile?.organisation_id);
  const { locationsData } = useLocationContext();
  const saveMappings = useSaveBloomreachMappings();

  const [bconnectBrands, setBconnectBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);
  const [locationMappings, setLocationMappings] = useState({});
  const [brandListSelections, setBrandListSelections] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasRestoredMappings, setHasRestoredMappings] = useState(false);
  const [fieldMappings, setFieldMappings] = useState(() =>
    normalizeBloomreachFieldMappings(initialFieldMappings)
  );
  const [syncConsent, setSyncConsent] = useState(true);
  const [restaurantNames, setRestaurantNames] = useState({});
  const [consentCategories, setConsentCategories] = useState([]);

  useEffect(() => {
    if (!isOpen || !brandsData || !locationsData) return;

    const defaultBrand = brandsData.find((b) => b.is_default);
    const brands = brandsData.filter((b) => !b.is_default) || [];
    const independent =
      locationsData.filter(
        (loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null
      ) || [];
    setBconnectBrands(brands);
    setIndependentLocations(independent);
    setBrandListSelections({});

    const brandMap = {};
    brands.forEach((b) => {
      const sourceBrand = brandsData.find((bd) => String(bd.id) === String(b.id));
      brandMap[b.id] =
        sourceBrand && Array.isArray(sourceBrand.locations)
          ? sourceBrand.locations
          : locationsData.filter((loc) => String(loc.brand_id) === String(b.id));
    });

    const savedRows = Array.isArray(initialMappings) ? initialMappings : [];
    const mappedIds = new Set(
      savedRows
        .filter((row) => row?.bconnect_location_id != null)
        .map((row) => String(row.bconnect_location_id))
    );
    const savedNames = {};
    savedRows.forEach((row) => {
      if (row?.bconnect_location_id == null) return;
      savedNames[String(row.bconnect_location_id)] = row.restaurant_name || row.location_name || "";
    });
    const allLocations = [...independent, ...Object.values(brandMap).flat()];
    const next = {};
    const nextNames = {};
    allLocations.forEach((loc) => {
      const id = locationId(loc);
      const shouldSync = mappedIds.size === 0 || mappedIds.has(id);
      next[id] = shouldSync ? BLOOMREACH_SYNC : BLOOMREACH_SKIP;
      nextNames[id] = savedNames[id] || loc.name || loc.label || "";
    });
    setLocationMappings(next);
    setRestaurantNames(nextNames);
    setConsentCategories(
      Array.isArray(initialConsentCategories) ? initialConsentCategories : []
    );
    setHasRestoredMappings(
      mappedIds.size > 0 &&
        (reconnectAction === "resume" || reconnectAction === "continue_from_now")
    );
    setFieldMappings(normalizeBloomreachFieldMappings(initialFieldMappings));
    setSyncConsent(initialSyncConsent !== false);
  }, [
    isOpen,
    brandsData,
    locationsData,
    initialMappings,
    initialFieldMappings,
    initialSyncConsent,
    initialConsentCategories,
    reconnectAction,
  ]);

  const brandLocationsMap = useMemo(() => {
    const map = {};
    (bconnectBrands || []).forEach((b) => {
      const sourceBrand = (brandsData || []).find((bd) => String(bd.id) === String(b.id));
      if (sourceBrand && Array.isArray(sourceBrand.locations)) {
        map[b.id] = sourceBrand.locations;
      } else {
        map[b.id] = (locationsData || []).filter(
          (loc) => String(loc.brand_id) === String(b.id)
        );
      }
    });
    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  const handleLocationChange = (id, value) => {
    setLocationMappings((prev) => ({ ...prev, [String(id)]: value }));
  };

  const handleApplyBrand = (brandId) => {
    const selected = brandListSelections[String(brandId)];
    if (!selected) return;
    const brandLocs = brandLocationsMap[brandId] || [];
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      brandLocs.forEach((loc) => {
        next[locationId(loc)] = selected;
      });
      return next;
    });
  };

  const handleSave = async () => {
    const allLocations = [
      ...(independentLocations || []),
      ...(Object.values(brandLocationsMap || {}).flat() || []),
    ];
    const locationPayload = allLocations
      .filter((loc) => locationMappings[locationId(loc)] === BLOOMREACH_SYNC)
      .map((loc) => ({
        bconnect_location_id: loc.id ?? loc.value ?? loc.location_id,
        bconnect_location_name: loc.name ?? loc.label ?? "",
        restaurant_name: restaurantNames[locationId(loc)] || loc.name || loc.label || "",
      }));

    if (locationPayload.length === 0) {
      setToastMessage("Select at least one location to sync.");
      return;
    }

    setIsLoading(true);
    try {
      const result = await saveMappings.mutateAsync({
        organisation_id: userProfile.organisation_id,
        location_mappings: locationPayload,
        field_mappings: fieldMappings,
        sync_consent: syncConsent,
        consent_categories: consentCategories,
      });
      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || "Failed to save location mappings");
      }

      queryClient.invalidateQueries(["bloomreach-status"]);
      queryClient.invalidateQueries(["bloomreach-mappings", userProfile.organisation_id]);
      onOpenChange(false);
      navigate("/activation-success", {
        state: {
          description: "Your customers will now sync with Bloomreach.",
          integrationType: "bloomreach",
        },
      });
    } catch (err) {
      setToastMessage(
        err?.response?.data?.message || err?.message || "Failed to save location mappings"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content
        maxWidth="720px"
        style={{
          height: "min(760px, calc(100vh - 96px))",
          padding: "28px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Flex direction="column" gap="4" style={{ flex: "1 1 auto", minHeight: 0 }}>
          <Flex direction="column" gap="2" style={{ flex: "0 0 auto" }}>
            <Dialog.Title>Choose locations to sync</Dialog.Title>
            <Dialog.Description size="2" style={{ color: "var(--gray-a11)" }}>
              {hasRestoredMappings
                ? "Your previous location mappings have been restored. Review them below, change anything if needed, then save to finish connecting."
                : "Choose which b connect locations should sync to Bloomreach. You can set each location independently."}
            </Dialog.Description>
          </Flex>

          <Box
            style={{
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
              paddingRight: "var(--space-2)",
            }}
          >
            <Flex direction="column" gap="4">
              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Brands
                  </Text>
                  <Text size="1" color="gray">
                    Group: b connect brands with each of their locations.
                  </Text>
                </Flex>

                <BloomreachMappingHeader />

                {(bconnectBrands || []).length > 0 ? (
                  (bconnectBrands || []).map((b) => {
                    const brandLocations = brandLocationsMap[b.id] || [];
                    return (
                      <Flex key={b.id} direction="column" gap="2" style={{ width: "100%" }}>
                        <BloomreachColumnGrid>
                          <Text
                            size="2"
                            weight="medium"
                            title={b.name}
                            style={{
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            [{b.name}]
                          </Text>
                          <span />
                          {(brandLocations || []).length > 0 ? (
                            <Flex gap="2" align="center" style={{ minWidth: 0 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                              <Select.Root
                                value={brandListSelections[String(b.id)] || ""}
                                onValueChange={(value) =>
                                  setBrandListSelections((prev) => ({
                                    ...prev,
                                    [String(b.id)]: value,
                                  }))
                                }
                              >
                                <Select.Trigger
                                  placeholder="Apply to brand..."
                                  style={{ width: "100%", boxSizing: "border-box" }}
                                >
                                  <div
                                    style={{
                                      textOverflow: "ellipsis",
                                      overflow: "hidden",
                                      whiteSpace: "nowrap",
                                      width: "100%",
                                    }}
                                  >
                                    {brandListSelections[String(b.id)] === BLOOMREACH_SYNC
                                      ? "Sync"
                                      : brandListSelections[String(b.id)] === BLOOMREACH_SKIP
                                      ? "Don't sync"
                                      : "Apply to brand..."}
                                  </div>
                                </Select.Trigger>
                                <Select.Content side="bottom" align="start" sideOffset={4}>
                                  <Select.Item value={BLOOMREACH_SYNC}>Sync</Select.Item>
                                  <Select.Item value={BLOOMREACH_SKIP}>Don't sync</Select.Item>
                                </Select.Content>
                              </Select.Root>
                              </div>
                              <Button
                                size="1"
                                variant="soft"
                                onClick={() => handleApplyBrand(b.id)}
                                style={{ flexShrink: 0 }}
                              >
                                Apply
                              </Button>
                            </Flex>
                          ) : (
                            <span />
                          )}
                        </BloomreachColumnGrid>

                        {(brandLocations || []).length > 0 ? (
                          brandLocations.map((loc) => (
                            <BloomreachLocationRow
                              key={locationId(loc)}
                              loc={loc}
                              value={locationMappings[locationId(loc)]}
                              restaurantName={restaurantNames[locationId(loc)] || ""}
                              onRestaurantNameChange={(value) =>
                                setRestaurantNames((prev) => ({ ...prev, [locationId(loc)]: value }))
                              }
                              onValueChange={(value) => handleLocationChange(locationId(loc), value)}
                            />
                          ))
                        ) : (
                          <Text size="2" style={{ color: "var(--gray-a9)" }}>
                            No locations for this brand
                          </Text>
                        )}
                      </Flex>
                    );
                  })
                ) : (
                  <Text size="2" color="gray">
                    No brands found.
                  </Text>
                )}
              </Flex>

              <Separator orientation="horizontal" size="4" />

              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Locations
                  </Text>
                  <Text size="1" color="gray">
                    Choose which independent b connect locations sync. The restaurant name on the WiFi event starts as the b connect location name. Change it when Bloomreach uses a different name.
                  </Text>
                </Flex>

                <BloomreachMappingHeader />

                {(independentLocations || []).length > 0 ? (
                  independentLocations.map((loc) => (
                    <BloomreachLocationRow
                      key={locationId(loc)}
                      loc={loc}
                      value={locationMappings[locationId(loc)]}
                      restaurantName={restaurantNames[locationId(loc)] || ""}
                      onRestaurantNameChange={(value) =>
                        setRestaurantNames((prev) => ({ ...prev, [locationId(loc)]: value }))
                      }
                      onValueChange={(value) => handleLocationChange(locationId(loc), value)}
                    />
                  ))
                ) : (
                  <Text size="2" color="gray">
                    No locations found.
                  </Text>
                )}
              </Flex>

              <Separator orientation="horizontal" size="4" />

              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Data fields
                  </Text>
                  <Text size="1" color="gray">
                    These fields are sent on each WiFi event. Email is always sent. Leave a
                    field as Don't sync to leave it off the event.
                  </Text>
                </Flex>
                <BloomreachDataFieldsSection
                  fieldMappings={fieldMappings}
                  onChange={(source, value) =>
                    setFieldMappings((prev) => ({ ...prev, [source]: value }))
                  }
                />
              </Flex>

              <Separator orientation="horizontal" size="4" />

              <Flex align="center" justify="between">
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Sync marketing consent
                  </Text>
                  <Text size="1" color="gray">
                    The WiFi form collects one marketing opt-in. Choose which Bloomreach categories that opt-in ticks.
                  </Text>
                </Flex>
                <Switch checked={syncConsent} onCheckedChange={setSyncConsent} />
              </Flex>
              {syncConsent ? (
                <BloomreachConsentSection
                  organisationId={userProfile?.organisation_id}
                  categories={consentCategories}
                  onChange={setConsentCategories}
                  enabled={isOpen}
                />
              ) : null}
            </Flex>
          </Box>

          <Flex justify="end" gap="3" style={{ flex: "0 0 auto" }}>
            <Dialog.Close>
              <Button size="2" variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button size="2" variant="soft" color="accent" loading={isLoading} onClick={handleSave}>
              Save and finish
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>

      <div style={{ position: "fixed", zIndex: 2147483647, pointerEvents: "none" }}>
        <ToastAlert />
      </div>
    </Dialog.Root>
  );
}
